/**
 * The product page. A14.
 *
 * Four independent pieces, each of which degrades to something that still
 * works: the variant picker (the form posts), the x-ray (the photograph), the
 * sticky bar (the buy box is still up the page) and true scale (the millimetre
 * figure is printed either way).
 */

const money = (paise) => {
  const rupees = String(Math.round(paise / 100));
  const head = rupees.slice(0, -3);
  const tail = rupees.slice(-3);
  return '₹' + (head ? head.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + tail : tail);
};

/* ------------------------------------------------------------ variant picker */

class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    this.addEventListener('change', () => this.update());
    this.update({ silent: true });
  }

  get chosen() {
    return [...this.querySelectorAll('.picker__input:checked')].map((i) => i.value);
  }

  update({ silent = false } = {}) {
    const chosen = this.chosen;
    const match = this.variants.find((v) => v.options.every((o, i) => o === chosen[i]));

    this.markUnavailable(chosen);

    const note = this.querySelector('[data-unavailable]');
    if (note) note.hidden = Boolean(match);

    if (!match) return;

    // The data is already here. Never a loading state, never a flash.
    document.querySelector('[data-price]').textContent = money(match.price);
    document.querySelector('[data-variant-input]').value = match.id;
    const addButton = document.querySelector('.buy-box__add');
    if (addButton) addButton.disabled = !match.available;

    const from = document.querySelector('[data-from]');
    if (from) from.hidden = true;

    const stickyPrice = document.querySelector('[data-sticky-price]');
    const stickyVariant = document.querySelector('[data-sticky-variant]');
    if (stickyPrice) stickyPrice.textContent = money(match.price);
    if (stickyVariant) stickyVariant.textContent = match.title;

    if (match.image) {
      const galleryImage = document.querySelector('[data-gallery-main]');
      if (galleryImage) {
        galleryImage.src = match.image;
        galleryImage.removeAttribute('srcset');
      }
    }

    this.syncSpecs(match);
    this.syncReceipt(match);
    this.syncMath(match);

    if (!silent) {
      // replaceState, never push — otherwise the back button walks her through
      // every combination she tried. interactions.md §3.3.
      const url = new URL(location.href);
      url.searchParams.set('variant', match.id);
      history.replaceState({}, '', url);
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: match }));
    }
  }

  syncReceipt(match) {
    const receipt = document.querySelector('[data-receipt]');
    if (!receipt) return;

    const values = match.receipt || {};
    const subtotal = Number(values.metal || 0) + Number(values.diamond || 0) + Number(values.polki || 0) + Number(values.making || 0);
    const total = subtotal + Number(values.gst || 0);
    const reconciles = Math.round(subtotal * 100) === Math.round(Number(values.subtotal || 0) * 100)
      && Math.round(total * 100) === Number(match.price);

    receipt.hidden = !reconciles;
    if (!reconciles) return;

    ['metal', 'diamond', 'polki', 'making', 'gst', 'subtotal'].forEach((key) => {
      const value = Number(values[key] || 0);
      const line = receipt.querySelector(`[data-receipt-line="${key}"]`);
      const cell = receipt.querySelector(`[data-receipt-value="${key}"]`);
      if (line) line.hidden = value <= 0;
      if (cell) cell.textContent = money(value * 100);
    });

    const totalCell = receipt.querySelector('[data-receipt-value="total"]');
    if (totalCell) totalCell.textContent = money(match.price);
  }

  /**
   * The x-ray, the specification list, true scale and the WhatsApp message all
   * describe the chosen variant, so they move with it. interactions.md §3.3
   * asks for this to be synchronous — the data is already on the page, so a
   * loading state here would be theatre.
   */
  syncSpecs(match) {
    const specs = match.specs || {};

    document.querySelectorAll('[data-spec]').forEach((cell) => {
      const value = specs[cell.dataset.spec];
      const row = cell.closest('.gallery__spec, .specs__row');
      if (value === null || value === undefined || value === '') {
        if (row) row.hidden = true;
        return;
      }
      if (row) row.hidden = false;
      cell.textContent = value + (cell.dataset.unit || '');
    });

    document.querySelectorAll('[data-stone-variant]').forEach((table) => {
      table.hidden = table.dataset.stoneVariant !== String(match.id);
    });

    // Exact equality, not a contains-match: a plating note that merely
    // mentions vermeil must not light the chip.
    const vermeilChip = document.querySelector('[data-vermeil-chip]');
    if (vermeilChip) vermeilChip.hidden = specs.plating !== 'Gold Vermeil';

    // The picker's first update runs before customElements upgrades this
    // element, so it is a plain HTMLElement with no draw() for one tick.
    const scale = document.querySelector('true-scale');
    if (scale && typeof scale.draw === 'function') {
      // The measurement belongs to the variant, not to the product, so a
      // variant nobody has measured takes the block off the page. Leaving the
      // last variant's millimetres standing would draw a ring she is no longer
      // looking at, at a size it is not.
      const mm = Number(specs.dimensionMm);
      const host = scale.closest('.true-scale, .accordion') || scale;
      host.hidden = !(mm > 0);
      if (mm > 0) {
        scale.dataset.mm = String(mm);
        scale.mm = mm;
        scale.draw();
        const label = scale.querySelector('[data-size]');
        if (label) label.textContent = `${mm} mm`;
      }
    }

    const message = document.querySelector('[data-wa-message]');
    if (message && match.sku) {
      message.value = message.value.replace(/\[[^\]]*\]/, `[${match.sku}]`);
    }
  }

  /** Per wear is price over the register's divisor, and the price just moved. */
  syncMath(match) {
    const math = document.querySelector('[data-math]');
    const figure = math && math.querySelector('[data-per-wear]');
    if (!figure) return;
    const divisor = Number(math.dataset.divisor);
    if (!divisor) return;
    figure.textContent = money(match.price / divisor);
  }

  /**
   * A combination that does not exist is greyed with a reason, never removed —
   * a row that reshuffles as she chooses is a row she cannot learn.
   */
  markUnavailable(chosen) {
    this.querySelectorAll('.picker__input').forEach((input) => {
      const position = Number(input.dataset.optionPosition) - 1;
      const trial = [...chosen];
      trial[position] = input.value;

      const exists = this.variants.some(
        (v) => v.available && v.options.every((o, i) => (i === position ? o === input.value : o === trial[i]))
      );

      const chip = this.querySelector(`label[for="${input.id}"]`);
      if (chip) chip.setAttribute('aria-disabled', String(!exists));
    });
  }
}

customElements.define('variant-picker', VariantPicker);

/* --------------------------------------------------------------------- x-ray */

const HOLD_MS = 300;

const stage = document.querySelector('[data-xray-stage]');
if (stage) {
  const panel = stage.querySelector('[data-xray]');
  let timer = null;

  panel.querySelectorAll('.gallery__spec').forEach((row, i) => {
    row.style.setProperty('--spec-index', String(i));
  });

  const show = () => {
    stage.removeAttribute('data-zoomed');
    panel.hidden = false;
    requestAnimationFrame(() => panel.setAttribute('data-revealed', ''));
  };

  const start = (event) => {
    // Stop iOS offering to copy the image out from under the gesture.
    if (event.type === 'touchstart') event.preventDefault();
    clearTimeout(timer);
    timer = setTimeout(show, HOLD_MS);
  };

  const end = () => {
    clearTimeout(timer);
    panel.removeAttribute('data-revealed');
    panel.hidden = true;
  };

  stage.addEventListener('mousedown', start);
  stage.addEventListener('mouseup', end);
  stage.addEventListener('mouseleave', end);
  stage.addEventListener('touchstart', start, { passive: false });
  stage.addEventListener('touchend', end);
  stage.addEventListener('touchcancel', end);

  // Keyboard has no press-and-hold, so space toggles it instead.
  stage.addEventListener('keydown', (event) => {
    if (event.key !== ' ' && event.key !== 'Enter') return;
    event.preventDefault();
    panel.hidden ? show() : end();
  });
  stage.addEventListener('blur', end);
}

/* -------------------------------------------------------------------- thumbs */

const main = document.querySelector('[data-gallery-main]');
const galleryStage = document.querySelector('.gallery__stage');
document.querySelectorAll('[data-thumb]').forEach((thumb) => {
  thumb.addEventListener('click', () => {
    if (!main) return;
    galleryStage?.removeAttribute('data-zoomed');
    main.src = thumb.dataset.src;
    main.removeAttribute('srcset');
    const caption = document.querySelector('[data-gallery-caption]');
    if (caption) caption.textContent = thumb.dataset.caption || main.alt;
    document.querySelectorAll('[data-thumb]').forEach((t) => t.setAttribute('aria-pressed', 'false'));
    thumb.setAttribute('aria-pressed', 'true');
  });
});

const thumbs = [...document.querySelectorAll('[data-thumb]')];
const moveGallery = (step) => {
  if (!thumbs.length) return;
  const active = Math.max(0, thumbs.findIndex((thumb) => thumb.getAttribute('aria-pressed') === 'true'));
  thumbs[(active + step + thumbs.length) % thumbs.length].click();
};
document.querySelector('[data-gallery-prev]')?.addEventListener('click', () => moveGallery(-1));
document.querySelector('[data-gallery-next]')?.addEventListener('click', () => moveGallery(1));
/* The arrows sit on the image. Their presses and hovers stay theirs, so they
   neither start the X-ray hold nor drive the hover zoom underneath. */
const galleryArrows = document.querySelector('.gallery__arrows');
['mousedown', 'mouseup', 'touchstart', 'touchend', 'pointerdown', 'pointermove'].forEach((type) => {
  galleryArrows?.addEventListener(type, (event) => event.stopPropagation(), { passive: true });
});

/* Desktop zoom stays inside the image. The pointer position is the transform
   origin, so moving over a detail magnifies that detail without leaving the
   product page or hiding the surrounding context. */
if (galleryStage && main && matchMedia('(hover: hover) and (pointer: fine)').matches) {
  galleryStage.addEventListener('pointermove', (event) => {
    const xray = galleryStage.querySelector('[data-xray]');
    if (xray && !xray.hidden) return;
    const bounds = galleryStage.getBoundingClientRect();
    galleryStage.style.setProperty('--zoom-x', `${((event.clientX - bounds.left) / bounds.width) * 100}%`);
    galleryStage.style.setProperty('--zoom-y', `${((event.clientY - bounds.top) / bounds.height) * 100}%`);
    galleryStage.setAttribute('data-zoomed', '');
  });
  galleryStage.addEventListener('pointerleave', () => galleryStage.removeAttribute('data-zoomed'));
}

/* ---------------------------------------------------------------- sticky bar */

const sticky = document.querySelector('[data-sticky]');
const actions = document.querySelector('.buy-box__actions');
/**
 * The bar appears once the real buttons have scrolled off the top, and not a
 * moment before — while Add to bag is still reachable, a second copy of it is
 * just clutter.
 *
 * A scroll listener rather than an IntersectionObserver: the observer fires on
 * threshold crossings, and a fast programmatic jump past the element can skip
 * the crossing entirely and leave the bar in whichever state it was last in.
 * This is the same rAF-coalesced pattern header.js uses.
 */
if (sticky && actions) {
  let queued = false;

  const measure = () => {
    queued = false;
    sticky.hidden = actions.getBoundingClientRect().bottom > 0;
  };

  const onScroll = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(measure);
  };

  addEventListener('scroll', onScroll, { passive: true });
  addEventListener('resize', onScroll, { passive: true });
  measure();

  document.querySelector('[data-sticky-add]')?.addEventListener('click', () => {
    document.querySelector('[data-add-form]')?.requestSubmit();
  });
}

/* ------------------------------------------------------------------ add to bag */

const form = document.querySelector('[data-add-form]');
form?.addEventListener('submit', async (event) => {
  event.preventDefault();
  const label = form.querySelector('[data-add-label]');
  const rest = label.textContent.trim();

  try {
    const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: form.querySelector('[data-variant-input]').value,
        quantity: 1,
        properties: (() => {
          const size = document.querySelector('input[name="ring-size"]:checked');
          return size ? { 'Ring size': size.value } : {};
        })()
      })
    });
    if (!response.ok) throw new Error(String(response.status));

    label.textContent = label.dataset.confirmed || '✓ Added';
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    setTimeout(() => (label.textContent = rest), 1200);
  } catch {
    let error = form.querySelector('.card__error');
    if (!error) {
      error = document.createElement('p');
      error.className = 'card__error';
      error.setAttribute('role', 'alert');
      form.append(error);
    }
    error.textContent = form.dataset.error || 'That did not go through. Try again.';
  }
});

/* ------------------------------------------------------------------ whatsapp */

const wa = document.querySelector('[data-wa]');
if (wa) {
  const field = wa.querySelector('[data-wa-message]');
  const send = wa.querySelector('[data-wa-send]');
  const base = send.getAttribute('href');

  const sync = () => {
    send.href = base + '?text=' + encodeURIComponent(field.value.trim());
  };

  const fit = () => {
    field.style.height = 'auto';
    field.style.height = `${field.scrollHeight}px`;
  };

  field.addEventListener('input', () => {
    sync();
    fit();
  });
  document.addEventListener('variant:change', (event) => {
    field.value = field.value.replace(/\(([^)]*)\)/, `(${event.detail.title})`);
    sync();
    fit();
  });
  // The picker rewrites the SKU on the same tick; pick the change up either way.
  field.addEventListener('change', sync);
  sync();
  fit();
}

/* ---------------------------------------------------------------- true scale */

class TrueScale extends HTMLElement {
  connectedCallback() {
    this.slider = this.querySelector('[data-cal]');
    this.coin = this.querySelector('[data-coin]');
    this.piece = this.querySelector('[data-piece]');
    this.mm = Number(this.dataset.mm) || 10;

    // A ₹10 coin is 27mm across. Everything on screen is measured against it.
    this.slider.addEventListener('input', () => this.draw());
    this.draw();
  }

  draw() {
    // CSS millimetres assume 96dpi, which almost no screen actually is. The
    // slider is the correction: she drags until the drawn coin matches the one
    // in her hand, and everything else is drawn against that.
    const perMm = 3.7795 * (Number(this.slider.value) / 100);
    this.style.setProperty('--coin-size', `${(27 * perMm).toFixed(1)}px`);
    this.style.setProperty('--piece-size', `${(this.mm * perMm).toFixed(1)}px`);
  }
}

customElements.define('true-scale', TrueScale);

/* ------------------------------------------------------------- option note */

/**
 * The line under the finish row. interactions.md §3.4: it shows the selected
 * option at rest, follows hover on a pointer, and returns on mouse-out. On
 * touch it simply follows selection, because there is no hover to follow.
 *
 * The container reserves two lines in CSS, so none of this moves the page.
 */
const noteBox = document.querySelector('[data-option-note]');
if (noteBox) {
  const notes = [...noteBox.querySelectorAll('[data-for]')];

  const show = (value) => {
    const match = notes.find((n) => n.dataset.for === value);
    if (!match) return;
    notes.forEach((n) => (n.hidden = n !== match));
  };

  const selected = () =>
    [...document.querySelectorAll('.picker__input:checked')].map((i) => i.value);

  const settle = () => {
    const value = selected().find((v) => notes.some((n) => n.dataset.for === v));
    if (value) show(value);
  };

  document.querySelectorAll('.picker__chip').forEach((chip) => {
    const value = document.getElementById(chip.htmlFor)?.value;
    if (!value) return;
    chip.addEventListener('mouseenter', () => show(value));
    chip.addEventListener('mouseleave', settle);
  });

  document.addEventListener('variant:change', settle);
  settle();
}

/* --------------------------------------------------------------- size block */

/**
 * Two paths to the same purchase. "I don't know" is not a failure state — it
 * carries the measuring and free-resizing guidance instead of another selector.
 */
const sizeBlock = document.querySelector('[data-size-block]');
if (sizeBlock) {
  const paths = [...sizeBlock.querySelectorAll('[data-path]')];
  const panels = [...sizeBlock.querySelectorAll('[data-panel]')];

  const open = (name) => {
    paths.forEach((p) => p.setAttribute('aria-selected', String(p.dataset.path === name)));
    panels.forEach((p) => (p.hidden = p.dataset.panel !== name));
  };

  paths.forEach((path) => {
    path.addEventListener('click', () => open(path.dataset.path));
    path.addEventListener('keydown', (event) => {
      const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
      if (!step) return;
      event.preventDefault();
      const next = paths[(paths.indexOf(path) + step + paths.length) % paths.length];
      next.focus();
      open(next.dataset.path);
    });
  });

  /*
   * The chip grid opens on demand below the desktop breakpoint. The trigger
   * reports the chosen size, so collapsing never hides what she picked. CSS
   * owns which widths collapse; this only tracks state.
   */
  const toggle = sizeBlock.querySelector('[data-size-toggle]');
  const toggleLabel = sizeBlock.querySelector('[data-size-toggle-label]');
  if (toggle && toggleLabel) {
    const prompt = toggleLabel.textContent;

    const showChosen = () => {
      const picked = sizeBlock.querySelector('.size-block__size:has(input:checked)');
      toggleLabel.textContent = picked
        ? [...picked.querySelectorAll('span')].map((s) => s.textContent.trim()).join(' \u00B7 ')
        : prompt;
    };

    toggle.addEventListener('click', () => {
      toggle.setAttribute('aria-expanded', String(toggle.getAttribute('aria-expanded') !== 'true'));
    });

    sizeBlock.addEventListener('change', showChosen);
    showChosen();
  }
}

/* ------------------------------------------------------------------ scenes */

/**
 * interactions.md §3.5: a proper tablist, arrow keys move between tabs, and the
 * swap is a crossfade rather than a slide — the piece is meant to hold still
 * while the day around it changes.
 */
const scenes = document.querySelector('[data-scenes]');
if (scenes) {
  const tabs = [...scenes.querySelectorAll('[data-scene]')];
  const panels = [...scenes.querySelectorAll('[data-scene-panel]')];

  const show = (name) => {
    tabs.forEach((t) => t.setAttribute('aria-selected', String(t.dataset.scene === name)));
    panels.forEach((p) => (p.hidden = p.dataset.scenePanel !== name));
  };

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => show(tab.dataset.scene));
    tab.addEventListener('keydown', (event) => {
      const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
      if (!step) return;
      event.preventDefault();
      const next = tabs[(tabs.indexOf(tab) + step + tabs.length) % tabs.length];
      next.focus();
      show(next.dataset.scene);
    });
  });
}
